'use client';

import React, { useState, useEffect } from 'react';
import { X, BookOpen, ShieldCheck, BarChart2, Clock, CheckCircle2, Circle, ChevronLeft, ChevronRight, Star, Lock,  } from 'lucide-react';

type TopicKey = 'Strategy Fundamentals' | 'Risk Management' | 'Chart Patterns';
type Status = 'completed' | 'in-progress' | 'locked' | 'available';

interface Module {
  id: string;
  title: string;
  description: string;
  topic: TopicKey;
  duration: string;
  lessons: number;
  status: Status;
  progress: number;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  featured?: boolean;
}

interface Lesson {
  id: string;
  title: string;
  content: string[];
  chartType: 'trend' | 'breakout' | 'reversion' | 'position' | 'stoploss' | 'rr' | 'reversal' | 'continuation' | 'candlestick' | 'volume' | 'none';
  keyPoints: string[];
}

// Lesson content data keyed by module id
const lessonData: Record<string, Lesson[]> = {
  'sf-1': [
    {
      id: 'sf-1-l1', title: 'What Is a Trend?',
      content: [
        'A trend is the general direction in which price is moving over a given period. Markets move in three directions: uptrend (higher highs and higher lows), downtrend (lower highs and lower lows), and sideways (ranging).',
        'Identifying the trend is the single most important skill for any trader. Trading with the trend dramatically increases the probability of a successful trade because you are aligned with the dominant market force.',
      ],
      chartType: 'trend',
      keyPoints: ['Higher highs + higher lows = uptrend', 'Lower highs + lower lows = downtrend', 'Always trade in the direction of the dominant trend'],
    },
    {
      id: 'sf-1-l2', title: 'Moving Averages as Trend Filters',
      content: [
        'Moving averages smooth out price noise and reveal the underlying trend direction. The 20 EMA acts as a dynamic support in uptrends; price consistently bouncing off it signals trend strength.',
        'The 50 SMA and 200 SMA are widely watched by institutional traders. When price is above the 200 SMA, the long-term trend is bullish. Crossovers between the 50 and 200 SMA generate the classic "Golden Cross" and "Death Cross" signals.',
      ],
      chartType: 'trend',
      keyPoints: ['20 EMA = short-term trend filter', '200 SMA = long-term bias', 'Golden Cross: 50 SMA crosses above 200 SMA'],
    },
    {
      id: 'sf-1-l3', title: 'Price Action Confirmation',
      content: [
        'Price action is the purest form of trend analysis. Look for clean impulse legs (strong directional moves) followed by shallow pullbacks. The shallower the pullback, the stronger the trend.',
        'A trend remains intact as long as the sequence of higher highs and higher lows (or lower highs and lower lows) is preserved. A break of the most recent swing low in an uptrend is the first warning sign of a potential reversal.',
      ],
      chartType: 'trend',
      keyPoints: ['Impulse legs should be larger than pullbacks', 'Trend ends when swing structure breaks', 'Volume should expand on impulse, contract on pullback'],
    },
  ],
  'sf-2': [
    {
      id: 'sf-2-l1', title: 'Identifying Breakout Zones',
      content: [
        'A breakout occurs when price moves decisively beyond a key level of support or resistance. The best breakouts come from well-defined consolidation zones where price has tested the level multiple times.',
        'The more times a level is tested without breaking, the more significant the eventual breakout. Each failed test adds more trapped traders on the wrong side, fueling the explosive move when the level finally gives way.',
      ],
      chartType: 'breakout',
      keyPoints: ['Multiple tests = stronger breakout', 'Look for tight consolidation before the break', 'Pre-breakout volume contraction is bullish'],
    },
    {
      id: 'sf-2-l2', title: 'Volume Confirmation',
      content: [
        'Volume is the fuel behind breakout moves. A genuine breakout should be accompanied by a significant surge in volume — ideally 1.5x to 2x the average daily volume. This confirms institutional participation.',
        'A breakout on low volume is suspect and often leads to a false breakout (fakeout). Always wait for the candle to close above the breakout level with elevated volume before entering.',
      ],
      chartType: 'breakout',
      keyPoints: ['Volume surge validates the breakout', 'Low-volume breakouts often fail', 'Wait for candle close above resistance'],
    },
  ],
  'sf-3': [
    {
      id: 'sf-3-l1', title: 'Understanding Mean Reversion',
      content: [
        'Mean reversion is the tendency of price to return to its average after an extreme move. When price deviates significantly from its mean (e.g., the 20 EMA), it tends to snap back, creating high-probability entry opportunities.',
        'The key is identifying when price is "overextended." Tools like RSI, Bollinger Bands, and distance from moving averages help quantify how far price has stretched from its mean.',
      ],
      chartType: 'reversion',
      keyPoints: ['Price always returns to the mean', 'RSI > 70 or < 30 signals overextension', 'Best used in ranging or weakly trending markets'],
    },
  ],
  'sf-4': [
    {
      id: 'sf-4-l1', title: 'The Top-Down Approach',
      content: [
        'Multi-timeframe analysis starts with the highest timeframe (monthly/weekly) to establish the macro trend and key levels. You then drill down to the daily for structure, and finally to the 4H or 1H for entry timing.',
        'The rule is simple: only take trades in the direction of the higher timeframe trend. A buy signal on the 1H chart carries far more weight when the daily and weekly charts are also bullish.',
      ],
      chartType: 'trend',
      keyPoints: ['Start with weekly/monthly for macro bias', 'Daily chart for key S/R levels', '4H/1H for precise entry timing'],
    },
  ],
  'rm-1': [
    {
      id: 'rm-1-l1', title: 'The 1% Rule',
      content: [
        'The 1% rule states that you should never risk more than 1% of your total trading account on a single trade. This ensures that even a long losing streak cannot wipe out your account.',
        'For a $10,000 account, maximum risk per trade is $100. This sounds small, but it allows you to survive 20+ consecutive losses and still have 82% of your capital intact. Consistency and survival are the foundation of long-term profitability.',
      ],
      chartType: 'position',
      keyPoints: ['Risk max 1% per trade', '$10k account = $100 max risk per trade', '20 losses in a row still leaves 82% of capital'],
    },
    {
      id: 'rm-1-l2', title: 'ATR-Based Position Sizing',
      content: [
        'The Average True Range (ATR) measures market volatility. Using ATR to set your stop loss ensures your stop is placed at a statistically meaningful distance — not too tight to be stopped out by noise, not too wide to risk too much.',
        'Formula: Position Size = (Account Risk $) ÷ (ATR × Multiplier). A common multiplier is 1.5x to 2x ATR. This method automatically adjusts your position size based on current market conditions.',
      ],
      chartType: 'position',
      keyPoints: ['ATR = market volatility measure', 'Stop = 1.5–2x ATR from entry', 'Position size shrinks in volatile markets automatically'],
    },
  ],
  'rm-2': [
    {
      id: 'rm-2-l1', title: 'Structure-Based Stops',
      content: [
        'The best stop losses are placed at logical market structure levels — just below the most recent swing low for longs, or just above the most recent swing high for shorts. These are levels where the market has already shown it respects price.',
        'Avoid placing stops at round numbers or arbitrary pip distances. The market does not care about your stop — it cares about structure. Place your stop where your trade thesis is definitively invalidated.',
      ],
      chartType: 'stoploss',
      keyPoints: ['Stop below swing low (long) or above swing high (short)', 'Add a small buffer beyond the structure level', 'Stop placement determines position size, not the other way around'],
    },
  ],
  'rm-3': [
    {
      id: 'rm-3-l1', title: 'Calculating Risk-Reward Ratio',
      content: [
        'The Risk-Reward Ratio (R:R) compares the potential profit of a trade to its potential loss. A 1:2 R:R means you risk $1 to make $2. With a 50% win rate and 1:2 R:R, you are profitable.',
        'Aim for a minimum 1:2 R:R on every trade. This means your target must be at least twice as far from your entry as your stop loss. Trades that do not meet this threshold should be skipped, regardless of how good the setup looks.',
      ],
      chartType: 'rr',
      keyPoints: ['Minimum 1:2 R:R on every trade', 'Win rate of 40% is profitable with 1:3 R:R', 'Skip setups that do not meet your R:R threshold'],
    },
  ],
  'rm-4': [
    {
      id: 'rm-4-l1', title: 'Defining Your Drawdown Limit',
      content: [
        'Every trader must define a maximum drawdown limit — the point at which they stop trading and reassess. A common rule is to stop trading if you lose 10% of your account in a single month.',
        'Drawdown limits prevent emotional decision-making from compounding losses. When you hit your limit, step back, review your trades, identify what went wrong, and only return when you have a clear plan.',
      ],
      chartType: 'none',
      keyPoints: ['Set a monthly max drawdown (e.g., 10%)', 'Stop trading when limit is hit', 'Review and diagnose before resuming'],
    },
  ],
  'cp-1': [
    {
      id: 'cp-1-l1', title: 'Head & Shoulders Pattern',
      content: [
        'The Head & Shoulders (H&S) is one of the most reliable reversal patterns. It consists of three peaks: a left shoulder, a higher head, and a right shoulder at roughly the same level as the left. The neckline connects the two troughs between the peaks.',
        'A confirmed H&S breakdown occurs when price closes below the neckline with elevated volume. The measured move target is calculated by subtracting the height of the head from the neckline breakout point.',
      ],
      chartType: 'reversal',
      keyPoints: ['Three peaks: left shoulder, head, right shoulder', 'Neckline break = entry signal', 'Target = head height projected from neckline'],
    },
    {
      id: 'cp-1-l2', title: 'Double Tops & Bottoms',
      content: [
        'A double top forms when price tests a resistance level twice and fails both times, creating an "M" shape. It signals that buyers are exhausted and sellers are taking control. Confirmation comes on a break below the trough between the two peaks.',
        'A double bottom is the mirror image — an "W" shape at support. It signals buyer absorption of selling pressure. Entry is on a break above the peak between the two troughs, with a target equal to the height of the pattern.',
      ],
      chartType: 'reversal',
      keyPoints: ['Double top = bearish reversal at resistance', 'Double bottom = bullish reversal at support', 'Confirm with volume on the breakout candle'],
    },
  ],
  'cp-2': [
    {
      id: 'cp-2-l1', title: 'Bull & Bear Flags',
      content: [
        'A bull flag forms after a strong upward impulse (the flagpole), followed by a tight, downward-sloping consolidation (the flag). The consolidation represents profit-taking, not a reversal. Entry is on a breakout above the upper flag boundary.',
        'The measured move target is the length of the flagpole added to the breakout point. Bull flags are most reliable when the flagpole is steep and the consolidation is shallow (less than 50% retracement of the pole).',
      ],
      chartType: 'continuation',
      keyPoints: ['Flagpole = strong impulse move', 'Flag = tight, shallow consolidation', 'Target = flagpole length from breakout'],
    },
  ],
  'cp-3': [
    {
      id: 'cp-3-l1', title: 'Engulfing Candles',
      content: [
        'A bullish engulfing candle occurs when a large green candle completely engulfs the body of the previous red candle. It signals a powerful shift in momentum from sellers to buyers and is most significant at key support levels or after a downtrend.',
        'The bearish engulfing is the mirror image — a large red candle engulfing the previous green candle at resistance. The larger the engulfing candle relative to the previous candle, the stronger the signal.',
      ],
      chartType: 'candlestick',
      keyPoints: ['Engulfing body must fully cover previous candle body', 'Most powerful at key S/R levels', 'Confirm with volume on the engulfing candle'],
    },
  ],
  'cp-4': [
    {
      id: 'cp-4-l1', title: 'Volume Precedes Price',
      content: [
        'Volume is a leading indicator — it often signals a change in trend before price confirms it. Increasing volume during a downtrend that suddenly spikes on an up-day suggests accumulation by smart money.',
        'On Balance Volume (OBV) tracks cumulative volume flow. A divergence between OBV and price (OBV rising while price falls) is a powerful bullish signal, indicating that buyers are absorbing supply at lower prices.',
      ],
      chartType: 'volume',
      keyPoints: ['Volume divergence precedes price reversal', 'OBV rising while price falls = bullish divergence', 'High volume on breakout = institutional confirmation'],
    },
  ],
};

// SVG Chart Components
function TrendChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {/* Grid lines */}
      {[40, 80, 120].map(y => <line key={y} x1="20" y1={y} x2="380" y2={y} stroke="#1E2D3D" strokeWidth="1" />)}
      {/* Price line - uptrend */}
      <polyline points="20,130 60,115 90,120 130,95 160,100 200,75 230,80 270,55 300,60 340,35 380,40"
        fill="none" stroke="#00D4AA" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Higher highs markers */}
      {[[130,95],[200,75],[270,55],[340,35]].map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="4" fill="#00D4AA" opacity="0.9" />
      ))}
      {/* Higher lows markers */}
      {[[60,115],[160,100],[230,80],[300,60]].map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="4" fill="#F59E0B" opacity="0.9" />
      ))}
      {/* 20 EMA */}
      <polyline points="20,135 80,118 140,102 200,85 260,68 320,52 380,45"
        fill="none" stroke="#6366F1" strokeWidth="1.5" strokeDasharray="4,3" />
      {/* Labels */}
      <text x="350" y="30" fill="#00D4AA" fontSize="9" fontFamily="monospace">HH</text>
      <text x="290" y="55" fill="#F59E0B" fontSize="9" fontFamily="monospace">HL</text>
      <text x="370" y="42" fill="#6366F1" fontSize="8" fontFamily="monospace">20 EMA</text>
    </svg>
  );
}

function BreakoutChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {[40, 80, 120].map(y => <line key={y} x1="20" y1={y} x2="380" y2={y} stroke="#1E2D3D" strokeWidth="1" />)}
      {/* Resistance line */}
      <line x1="20" y1="70" x2="380" y2="70" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="6,3" />
      {/* Consolidation candles */}
      {[40,60,80,100,120,140,160,180,200,220,240,260].map((x, i) => {
        const isGreen = i % 2 === 0;
        const bodyH = 15 + (i % 3) * 5;
        const bodyY = 75 + (i % 2) * 5;
        return (
          <g key={x}>
            <line x1={x+5} y1={bodyY - 8} x2={x+5} y2={bodyY + bodyH + 8} stroke={isGreen ? '#00D4AA' : '#EF4444'} strokeWidth="1.5" />
            <rect x={x} y={bodyY} width="10" height={bodyH} fill={isGreen ? '#00D4AA' : '#EF4444'} opacity="0.8" rx="1" />
          </g>
        );
      })}
      {/* Breakout candle */}
      <line x1="285" y1="20" x2="285" y2="90" stroke="#00D4AA" strokeWidth="1.5" />
      <rect x="278" y="20" width="14" height="50" fill="#00D4AA" opacity="0.95" rx="1" />
      {/* Post-breakout */}
      <polyline points="285,35 320,25 360,15 380,10" fill="none" stroke="#00D4AA" strokeWidth="2" />
      {/* Labels */}
      <text x="25" y="65" fill="#EF4444" fontSize="9" fontFamily="monospace">RESISTANCE</text>
      <text x="270" y="15" fill="#00D4AA" fontSize="9" fontFamily="monospace">BREAKOUT</text>
    </svg>
  );
}

function ReversionChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {[40, 80, 120].map(y => <line key={y} x1="20" y1={y} x2="380" y2={y} stroke="#1E2D3D" strokeWidth="1" />)}
      {/* Mean line */}
      <line x1="20" y1="80" x2="380" y2="80" stroke="#6366F1" strokeWidth="1.5" strokeDasharray="5,3" />
      {/* Upper band */}
      <line x1="20" y1="40" x2="380" y2="40" stroke="#F59E0B" strokeWidth="1" strokeDasharray="3,3" opacity="0.6" />
      {/* Lower band */}
      <line x1="20" y1="120" x2="380" y2="120" stroke="#F59E0B" strokeWidth="1" strokeDasharray="3,3" opacity="0.6" />
      {/* Price oscillating */}
      <polyline points="20,80 50,55 80,80 110,110 140,80 170,45 200,80 230,115 260,80 290,50 320,80 350,118 380,80"
        fill="none" stroke="#00D4AA" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Entry arrows */}
      <text x="155" y="42" fill="#00D4AA" fontSize="14">↓</text>
      <text x="215" y="125" fill="#00D4AA" fontSize="14">↑</text>
      <text x="25" y="78" fill="#6366F1" fontSize="9" fontFamily="monospace">MEAN</text>
      <text x="25" y="38" fill="#F59E0B" fontSize="9" fontFamily="monospace">+2σ</text>
      <text x="25" y="118" fill="#F59E0B" fontSize="9" fontFamily="monospace">-2σ</text>
    </svg>
  );
}

function PositionChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {/* Account bars */}
      {[
        { label: '$10k', risk: 100, pct: '1%', x: 40, color: '#00D4AA' },
        { label: '$25k', risk: 250, pct: '1%', x: 140, color: '#00D4AA' },
        { label: '$50k', risk: 500, pct: '1%', x: 240, color: '#00D4AA' },
        { label: '$100k', risk: 1000, pct: '1%', x: 340, color: '#00D4AA' },
      ].map(({ label, risk, pct, x, color }) => (
        <g key={x}>
          <rect x={x} y={60} width={50} height={70} fill={color} opacity="0.15" rx="4" />
          <rect x={x} y={120} width={50} height={10} fill={color} opacity="0.8" rx="2" />
          <text x={x+25} y={55} fill="#94A3B8" fontSize="9" textAnchor="middle" fontFamily="monospace">{label}</text>
          <text x={x+25} y={110} fill={color} fontSize="10" textAnchor="middle" fontFamily="monospace">${risk}</text>
          <text x={x+25} y={148} fill="#94A3B8" fontSize="8" textAnchor="middle" fontFamily="monospace">{pct} risk</text>
        </g>
      ))}
      <text x="200" y="20" fill="#94A3B8" fontSize="10" textAnchor="middle" fontFamily="monospace">MAX RISK PER TRADE (1% RULE)</text>
    </svg>
  );
}

function StopLossChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {[40, 80, 120].map(y => <line key={y} x1="20" y1={y} x2="380" y2={y} stroke="#1E2D3D" strokeWidth="1" />)}
      {/* Support level */}
      <line x1="20" y1="110" x2="380" y2="110" stroke="#00D4AA" strokeWidth="1.5" strokeDasharray="5,3" />
      {/* Stop loss */}
      <line x1="20" y1="130" x2="380" y2="130" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="4,3" />
      {/* Entry */}
      <line x1="20" y1="75" x2="380" y2="75" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="4,3" />
      {/* Price action */}
      <polyline points="20,120 60,105 100,112 140,95 180,100 220,80 260,85 300,65 340,70 380,55"
        fill="none" stroke="#00D4AA" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Labels */}
      <text x="330" y="72" fill="#F59E0B" fontSize="9" fontFamily="monospace">ENTRY</text>
      <text x="320" y="107" fill="#00D4AA" fontSize="9" fontFamily="monospace">SUPPORT</text>
      <text x="330" y="127" fill="#EF4444" fontSize="9" fontFamily="monospace">STOP</text>
      {/* Arrow showing stop below support */}
      <line x1="370" y1="110" x2="370" y2="130" stroke="#EF4444" strokeWidth="1.5" markerEnd="url(#arrow)" />
    </svg>
  );
}

function RRChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {/* Entry line */}
      <line x1="40" y1="90" x2="360" y2="90" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="4,3" />
      {/* Stop line */}
      <line x1="40" y1="120" x2="360" y2="120" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="4,3" />
      {/* Target line */}
      <line x1="40" y1="30" x2="360" y2="30" stroke="#00D4AA" strokeWidth="1.5" strokeDasharray="4,3" />
      {/* Risk zone */}
      <rect x="40" y="90" width="320" height="30" fill="#EF4444" opacity="0.08" />
      {/* Reward zone */}
      <rect x="40" y="30" width="320" height="60" fill="#00D4AA" opacity="0.08" />
      {/* Brace for risk */}
      <line x1="30" y1="90" x2="30" y2="120" stroke="#EF4444" strokeWidth="2" />
      <text x="5" y="108" fill="#EF4444" fontSize="9" fontFamily="monospace">1R</text>
      {/* Brace for reward */}
      <line x1="30" y1="30" x2="30" y2="90" stroke="#00D4AA" strokeWidth="2" />
      <text x="5" y="63" fill="#00D4AA" fontSize="9" fontFamily="monospace">2R</text>
      {/* Labels */}
      <text x="350" y="27" fill="#00D4AA" fontSize="9" fontFamily="monospace">TARGET</text>
      <text x="350" y="87" fill="#F59E0B" fontSize="9" fontFamily="monospace">ENTRY</text>
      <text x="350" y="117" fill="#EF4444" fontSize="9" fontFamily="monospace">STOP</text>
      <text x="200" y="150" fill="#94A3B8" fontSize="10" textAnchor="middle" fontFamily="monospace">1:2 RISK-REWARD RATIO</text>
    </svg>
  );
}

function ReversalChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {[40, 80, 120].map(y => <line key={y} x1="20" y1={y} x2="380" y2={y} stroke="#1E2D3D" strokeWidth="1" />)}
      {/* H&S pattern */}
      {/* Left shoulder */}
      <polyline points="20,120 60,80 90,100" fill="none" stroke="#00D4AA" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Head */}
      <polyline points="90,100 130,45 170,100" fill="none" stroke="#00D4AA" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Right shoulder */}
      <polyline points="170,100 210,80 240,100" fill="none" stroke="#00D4AA" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Neckline */}
      <line x1="20" y1="100" x2="380" y2="100" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="5,3" />
      {/* Breakdown */}
      <polyline points="240,100 270,115 310,130 350,145 380,150" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Labels */}
      <text x="55" y="75" fill="#94A3B8" fontSize="8" textAnchor="middle" fontFamily="monospace">LS</text>
      <text x="130" y="40" fill="#94A3B8" fontSize="8" textAnchor="middle" fontFamily="monospace">HEAD</text>
      <text x="210" y="75" fill="#94A3B8" fontSize="8" textAnchor="middle" fontFamily="monospace">RS</text>
      <text x="300" y="97" fill="#F59E0B" fontSize="9" fontFamily="monospace">NECKLINE</text>
      <text x="310" y="145" fill="#EF4444" fontSize="9" fontFamily="monospace">BREAKDOWN</text>
    </svg>
  );
}

function ContinuationChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {[40, 80, 120].map(y => <line key={y} x1="20" y1={y} x2="380" y2={y} stroke="#1E2D3D" strokeWidth="1" />)}
      {/* Flagpole */}
      <line x1="60" y1="140" x2="160" y2="50" stroke="#00D4AA" strokeWidth="3" />
      {/* Flag consolidation */}
      <line x1="160" y1="50" x2="220" y2="65" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="4,2" />
      <line x1="160" y1="70" x2="220" y2="85" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="4,2" />
      <polyline points="160,50 175,58 190,62 205,68 220,65" fill="none" stroke="#94A3B8" strokeWidth="1.5" />
      <polyline points="160,70 175,75 190,78 205,82 220,85" fill="none" stroke="#94A3B8" strokeWidth="1.5" />
      {/* Breakout continuation */}
      <polyline points="220,65 250,45 290,25 330,15 370,10" fill="none" stroke="#00D4AA" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Labels */}
      <text x="90" y="105" fill="#00D4AA" fontSize="9" fontFamily="monospace">FLAGPOLE</text>
      <text x="165" y="95" fill="#F59E0B" fontSize="9" fontFamily="monospace">FLAG</text>
      <text x="290" y="35" fill="#00D4AA" fontSize="9" fontFamily="monospace">CONTINUATION</text>
    </svg>
  );
}

function CandlestickChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {[40, 80, 120].map(y => <line key={y} x1="20" y1={y} x2="380" y2={y} stroke="#1E2D3D" strokeWidth="1" />)}
      {/* Bearish candles leading up */}
      {[40, 70, 100, 130].map((x, i) => (
        <g key={x}>
          <line x1={x+8} y1={45+i*5} x2={x+8} y2={85+i*5} stroke="#EF4444" strokeWidth="1.5" />
          <rect x={x} y={55+i*5} width="16" height="20" fill="#EF4444" opacity="0.8" rx="1" />
        </g>
      ))}
      {/* Bullish engulfing candle */}
      <line x1="178" y1="55" x2="178" y2="130" stroke="#00D4AA" strokeWidth="1.5" />
      <rect x="168" y="60" width="20" height="60" fill="#00D4AA" opacity="0.9" rx="1" />
      <text x="158" y="148" fill="#00D4AA" fontSize="8" fontFamily="monospace">ENGULFING</text>
      {/* Post-reversal bullish candles */}
      {[210, 245, 280, 315, 350].map((x, i) => (
        <g key={x}>
          <line x1={x+8} y1={55-i*8} x2={x+8} y2={90-i*8} stroke="#00D4AA" strokeWidth="1.5" />
          <rect x={x} y={60-i*8} width="16" height="22" fill="#00D4AA" opacity="0.8" rx="1" />
        </g>
      ))}
      <text x="200" y="15" fill="#94A3B8" fontSize="9" textAnchor="middle" fontFamily="monospace">BULLISH ENGULFING REVERSAL</text>
    </svg>
  );
}

function VolumeChart() {
  return (
    <svg viewBox="0 0 400 160" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#0F1923" rx="8" />
      {/* Price line declining */}
      <polyline points="20,40 60,50 100,60 140,70 180,80 220,90 260,85 300,75 340,60 380,45"
        fill="none" stroke="#00D4AA" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Volume bars */}
      {[20,50,80,110,140,170,200,230,260,290,320,350].map((x, i) => {
        const isLate = i >= 7;
        const h = isLate ? 25 + i * 4 : 15 + (i % 3) * 8;
        return (
          <rect key={x} x={x} y={160 - h} width="20" height={h}
            fill={isLate ? '#00D4AA' : '#EF4444'} opacity={isLate ? 0.7 : 0.4} rx="1" />
        );
      })}
      {/* Divergence arrow */}
      <text x="200" y="15" fill="#94A3B8" fontSize="9" textAnchor="middle" fontFamily="monospace">PRICE ↓ VOLUME ↑ = BULLISH DIVERGENCE</text>
      <text x="310" y="58" fill="#00D4AA" fontSize="8" fontFamily="monospace">↑ VOL</text>
    </svg>
  );
}

const chartComponents: Record<string, React.ReactNode> = {
  trend: <TrendChart />,
  breakout: <BreakoutChart />,
  reversion: <ReversionChart />,
  position: <PositionChart />,
  stoploss: <StopLossChart />,
  rr: <RRChart />,
  reversal: <ReversalChart />,
  continuation: <ContinuationChart />,
  candlestick: <CandlestickChart />,
  volume: <VolumeChart />,
  none: null,
};

const topicMeta: Record<TopicKey, { icon: React.ReactNode; color: string; bg: string; border: string }> = {
  'Strategy Fundamentals': { icon: <BookOpen size={14} />, color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' },
  'Risk Management': { icon: <ShieldCheck size={14} />, color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20' },
  'Chart Patterns': { icon: <BarChart2 size={14} />, color: 'text-[#6366F1]', bg: 'bg-[#6366F1]/10', border: 'border-[#6366F1]/20' },
};

const difficultyColor: Record<string, string> = {
  Beginner: 'text-positive bg-positive-subtle',
  Intermediate: 'text-warning bg-warning-subtle',
  Advanced: 'text-negative bg-negative-subtle',
};

interface LessonModalProps {
  module: Module | null;
  onClose: () => void;
}

export default function LessonModal({ module, onClose }: LessonModalProps) {
  const [currentLessonIdx, setCurrentLessonIdx] = useState(0);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());

  useEffect(() => {
    setCurrentLessonIdx(0);
    setCompletedLessons(new Set());
  }, [module?.id]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!module) return null;

  const lessons = lessonData[module.id] ?? [];
  const currentLesson = lessons[currentLessonIdx];
  const meta = topicMeta[module.topic];
  const isLocked = module.status === 'locked';

  const toggleComplete = (lessonId: string) => {
    setCompletedLessons(prev => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });
  };

  const allDone = lessons.length > 0 && lessons.every(l => completedLessons.has(l.id));
  const completedCount = lessons.filter(l => completedLessons.has(l.id)).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-3xl max-h-[90vh] bg-card border border-border rounded-2xl flex flex-col overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${meta.bg} ${meta.color} border ${meta.border}`}>
                {meta.icon}
                <span>{module.topic}</span>
              </div>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${difficultyColor[module.difficulty]}`}>
                {module.difficulty}
              </span>
              {module.featured && (
                <div className="flex items-center gap-1 bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5">
                  <Star size={9} className="text-primary fill-primary" />
                  <span className="text-[10px] font-semibold text-primary">Featured</span>
                </div>
              )}
            </div>
            <h2 className="text-base font-bold text-foreground leading-snug">{module.title}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{module.description}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 border-b border-border flex-shrink-0 bg-background/40">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <Clock size={12} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{module.duration}</span>
              <span className="text-muted-foreground/40">·</span>
              <BookOpen size={12} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{lessons.length} lessons</span>
            </div>
            <span className="text-xs font-semibold font-mono-nums text-foreground">
              {completedCount}/{lessons.length} completed
            </span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: lessons.length > 0 ? `${(completedCount / lessons.length) * 100}%` : '0%' }}
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden min-h-0">

          {/* Lesson list sidebar */}
          <div className="w-52 flex-shrink-0 border-r border-border overflow-y-auto bg-background/30 py-3">
            {isLocked ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 px-4 text-center">
                <Lock size={20} className="text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Complete previous modules to unlock</p>
              </div>
            ) : lessons.length === 0 ? (
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">No lessons available yet.</p>
              </div>
            ) : (
              <ul className="space-y-0.5 px-2">
                {lessons.map((lesson, idx) => {
                  const isDone = completedLessons.has(lesson.id);
                  const isActive = idx === currentLessonIdx;
                  return (
                    <li key={lesson.id}>
                      <button
                        onClick={() => setCurrentLessonIdx(idx)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg flex items-start gap-2.5 transition-all duration-150 ${
                          isActive
                            ? 'bg-primary/10 border border-primary/20' :'hover:bg-muted/50 border border-transparent'
                        }`}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {isDone
                            ? <CheckCircle2 size={14} className="text-primary" />
                            : <Circle size={14} className={isActive ? 'text-primary' : 'text-muted-foreground'} />
                          }
                        </div>
                        <div className="min-w-0">
                          <p className={`text-[11px] font-medium leading-snug ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {idx + 1}. {lesson.title}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Lesson content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {isLocked ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Lock size={22} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Module Locked</p>
                  <p className="text-xs text-muted-foreground mt-1">Complete the prerequisite modules to unlock this content.</p>
                </div>
              </div>
            ) : !currentLesson ? (
              <p className="text-sm text-muted-foreground">Select a lesson to begin.</p>
            ) : (
              <>
                {/* Lesson title */}
                <div>
                  <h3 className="text-sm font-bold text-foreground">{currentLesson.title}</h3>
                  <div className="h-px bg-border mt-2" />
                </div>

                {/* Lesson text */}
                <div className="space-y-3">
                  {currentLesson.content.map((para, i) => (
                    <p key={i} className="text-sm text-muted-foreground leading-relaxed">{para}</p>
                  ))}
                </div>

                {/* Chart example */}
                {currentLesson.chartType !== 'none' && chartComponents[currentLesson.chartType] && (
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Chart Example</p>
                    <div className="w-full h-40 rounded-xl overflow-hidden border border-border bg-[#0F1923]">
                      {chartComponents[currentLesson.chartType]}
                    </div>
                  </div>
                )}

                {/* Key points */}
                {currentLesson.keyPoints.length > 0 && (
                  <div className="bg-primary/5 border border-primary/15 rounded-xl p-4">
                    <p className="text-[11px] font-semibold text-primary uppercase tracking-wider mb-2.5">Key Takeaways</p>
                    <ul className="space-y-1.5">
                      {currentLesson.keyPoints.map((pt, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-foreground">
                          <span className="text-primary mt-0.5 flex-shrink-0">▸</span>
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Completion checkbox */}
                <div className="border border-border rounded-xl p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Mark as Complete</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Check this off once you've understood the lesson</p>
                  </div>
                  <button
                    onClick={() => toggleComplete(currentLesson.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      completedLessons.has(currentLesson.id)
                        ? 'bg-primary/10 border border-primary/30 text-primary' :'bg-muted border border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
                    }`}
                  >
                    {completedLessons.has(currentLesson.id)
                      ? <><CheckCircle2 size={16} /> Done</>
                      : <><Circle size={16} /> Mark Done</>
                    }
                  </button>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-1">
                  <button
                    onClick={() => setCurrentLessonIdx(i => Math.max(0, i - 1))}
                    disabled={currentLessonIdx === 0}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={14} /> Previous
                  </button>

                  {allDone && (
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
                      <CheckCircle2 size={14} />
                      Module Complete!
                    </div>
                  )}

                  <button
                    onClick={() => setCurrentLessonIdx(i => Math.min(lessons.length - 1, i + 1))}
                    disabled={currentLessonIdx === lessons.length - 1}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
